import type { IconProps } from '@chakra-ui/react';
import type { ElementType } from 'react';

/**
 * Maps NovaIcon `size` / glyph `glyphSize` to Chakra `boxSize` theme tokens (`sizes.icon.*`).
 * Single source of truth — {@link NovaIconGlyphSize} is `keyof` this object.
 */
export const NOVA_ICON_SIZES = {
  s: 'icon.s',
  m: 'icon.m',
  l: 'icon.l',
} as const satisfies Record<string, NonNullable<IconProps['boxSize']>>;

export type NovaIconGlyphSize = keyof typeof NOVA_ICON_SIZES;

/**
 * Props for SVG glyphs used with {@link NovaIcon}. Optional `glyphSize` lets outline
 * icons adjust stroke or paths per kit; fill-only icons may ignore it. Implementations must **omit**
 * `glyphSize` when spreading onto Chakra `Icon` so it is not forwarded to the DOM.
 */
export type NovaIconGlyphProps = IconProps & {
  glyphSize?: NovaIconGlyphSize;
};

type NovaIconBase = Omit<IconProps, 'boxSize' | 'as' | 'size'> & {
  /** Kit size → theme token via {@link NOVA_ICON_SIZES} (not Chakra Icon recipe sizes). */
  size?: NovaIconGlyphSize;
};

type NovaIconGlyphElement = ElementType<NovaIconGlyphProps>;

/** Require exactly one of `icon` or `as` (both name the glyph component). */
export type NovaIconProps =
  | (NovaIconBase & { icon: NovaIconGlyphElement; as?: never })
  | (NovaIconBase & { as: NovaIconGlyphElement; icon?: never });
